/**
 * ArrayTool.jsx — Cycle 55 · Teach & Explain
 * ────────────────────────────────────────────
 * A rectangular array of dots (r × c). Optional split at column `sx`
 * and row `sy` to reveal partial products — the visual foundation of
 * the grid method and distributive property.
 *
 * State: ?r=4&c=6&sx=3&sy=2&partials=1
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

export function ArrayDisplay({ rows, cols, sx, sy, showPartials }) {
  const total = rows * cols;
  // Split coordinates: 0 = no split. Otherwise split between col `sx` and sx+1.
  const splitX = sx > 0 && sx < cols ? sx : 0;
  const splitY = sy > 0 && sy < rows ? sy : 0;

  const cellClass = (r, c) => {
    const topHalf = splitY === 0 ? true : r < splitY;
    const leftHalf = splitX === 0 ? true : c < splitX;
    // Four quadrants get different tints when both splits active.
    if (!splitX && !splitY) return "bg-sky-400";
    if (splitX && !splitY)  return leftHalf ? "bg-sky-400" : "bg-fuchsia-400";
    if (splitY && !splitX)  return topHalf ? "bg-sky-400" : "bg-amber-400";
    if (topHalf && leftHalf) return "bg-sky-400";
    if (topHalf && !leftHalf) return "bg-fuchsia-400";
    if (!topHalf && leftHalf) return "bg-amber-400";
    return "bg-emerald-400";
  };

  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="ar-canvas">
      <p className="text-center font-heading text-xl md:text-2xl font-black mb-3" data-testid="ar-caption">
        {rows} × {cols} = <span className="tabular-nums">{total}</span>
      </p>
      <div className="mx-auto inline-block" style={{ maxWidth: "100%" }}>
        <div className="grid gap-1"
             style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxWidth: 480 }}
             data-testid="ar-grid">
          {Array.from({ length: rows * cols }).map((_, i) => {
            const r = Math.floor(i / cols);
            const c = i % cols;
            const boundaryLeft = splitX && c === splitX ? "ml-2" : "";
            const boundaryTop  = splitY && r === splitY ? "mt-2" : "";
            return (
              <div key={i}
                   className={`${boundaryLeft} ${boundaryTop}`}
                   data-testid={`ar-cell-${r}-${c}`}>
                <div className={`aspect-square rounded-full border-2 border-slate-900 ${cellClass(r, c)} shadow-[0_1px_0_0_rgba(15,23,42,1)]`} />
              </div>
            );
          })}
        </div>
      </div>

      {showPartials && (splitX || splitY) && (
        <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="ar-partials">
          {(() => {
            const parts = [];
            const xs = splitX ? [splitX, cols - splitX] : [cols];
            const ys = splitY ? [splitY, rows - splitY] : [rows];
            const colors = ["bg-sky-100", "bg-fuchsia-100", "bg-amber-100", "bg-emerald-100"];
            let ci = 0;
            for (const yh of ys) for (const xw of xs) {
              parts.push({ yh, xw, color: colors[ci++ % colors.length] });
            }
            return parts.map((p, i) => (
              <div key={i} className={`rounded-2xl border-[3px] border-slate-900 ${p.color} px-3 py-2 text-center`}
                   data-testid={`ar-part-${i}`}>
                <p className="font-heading text-sm font-black tabular-nums">{p.yh} × {p.xw}</p>
                <p className="font-heading text-lg font-black tabular-nums">= {p.yh * p.xw}</p>
              </div>
            ));
          })()}
          <div className="rounded-2xl border-[3px] border-slate-900 bg-slate-900 text-white px-3 py-2 text-center" data-testid="ar-part-sum">
            <p className="text-[10px] uppercase tracking-widest">Sum</p>
            <p className="font-heading text-lg font-black tabular-nums">{total}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ArrayTool() {
  const [params, setParams] = useSearchParams();
  const rows = clampInt(params.get("r"),  1, 12, 4);
  const cols = clampInt(params.get("c"),  1, 12, 6);
  const sx   = clampInt(params.get("sx"), 0, cols, 0);
  const sy   = clampInt(params.get("sy"), 0, rows, 0);
  const showPartials = params.get("partials") !== "0";

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };

  const settingsPanel = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <label className="text-xs font-bold text-slate-700">Rows
        <input type="number" min="1" max="12" value={rows} data-testid="ar-rows"
               onChange={(e) => update({ r: clampInt(e.target.value, 1, 12, 4) })}
               className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700">Cols
        <input type="number" min="1" max="12" value={cols} data-testid="ar-cols"
               onChange={(e) => update({ c: clampInt(e.target.value, 1, 12, 6) })}
               className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700">Split col after
        <input type="number" min="0" max={cols} value={sx} data-testid="ar-sx"
               onChange={(e) => update({ sx: clampInt(e.target.value, 0, cols, 0) })}
               className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700">Split row after
        <input type="number" min="0" max={rows} value={sy} data-testid="ar-sy"
               onChange={(e) => update({ sy: clampInt(e.target.value, 0, rows, 0) })}
               className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700 flex items-end gap-2">
        <input type="checkbox" checked={showPartials} data-testid="ar-partials-toggle"
               onChange={(e) => update({ partials: e.target.checked ? null : "0" })}
               className="h-4 w-4" />
        <span>Show partial products</span>
      </label>
    </div>
  );

  return (
    <ManipulativeFrame
      slug="arrays"
      title="Arrays"
      description="Row × Column grid of dots. Split it to reveal partial products — the visual link between multiplication, area and the grid method."
      onReset={() => update({ r: 4, c: 6, sx: null, sy: null, partials: null })}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <ArrayDisplay rows={rows} cols={cols} sx={sx} sy={sy} showPartials={showPartials} />
    </ManipulativeFrame>
  );
}
